import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The screen the web app shows when it has already failed.
 *
 * ## Why this exists
 *
 * Both phone apps have had one for months. The web app had none, so any error
 * thrown while rendering unmounted the entire tree and left a white page —
 * no message, no stack, nothing in the interface to act on. The only way to
 * learn anything was to open the browser console, which is not something a
 * dispatcher is going to do, and not something a contractor reporting the
 * problem can be asked to do either.
 *
 * It cost an evening: a white screen on one route was diagnosed by reading
 * source and guessing, three times, because the running application was
 * incapable of saying what had gone wrong.
 *
 * ## Why it is written this plainly
 *
 * It runs when something else is already broken, so it depends on as little
 * as possible: no theme import, no icon set, no router, no query client.
 * Literal colours and plain elements. A fallback that needs the application
 * to be working is not a fallback.
 *
 * ## What it shows
 *
 * The message, and the component stack. Not because a contractor will read
 * them, but because they will screenshot them — and a screenshot naming the
 * component is the difference between a fix and an afternoon of guessing.
 */

interface State {
  error: Error | null;
  stack: string | null;
}

export class Boundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null, stack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ stack: info.componentStack ?? null });
    // Still logged, for anybody who does have the console open.
    console.error('Crewlark crashed while rendering:', error, info.componentStack);
  }

  override render() {
    const { error, stack } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#f6f8f7',
          color: '#202b32',
          padding: '48px 24px',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
        }}
      >
        <div style={{ margin: '0 auto', maxWidth: 680 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 8px' }}>
            Something went wrong on this screen.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: '#51605a', margin: '0 0 20px' }}>
            Nothing you did caused this and nothing has been lost. Reloading usually fixes it.
          </p>

          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#225d4d',
              color: '#ffffff',
              border: 0,
              borderRadius: 8,
              padding: '11px 18px',
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Reload the page
          </button>

          {/*
            * Shown, not hidden behind a toggle. The person looking at this is
            * reporting a bug, and what they can see is what gets reported.
            */}
          <div
            style={{
              marginTop: 28,
              padding: 16,
              background: '#ffffff',
              border: '1px solid #e2e8e5',
              borderRadius: 10,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#51605a', marginBottom: 8 }}>
              WHAT BROKE
            </div>
            <pre
              style={{
                margin: 0,
                fontSize: 12.5,
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#b24030',
              }}
            >
              {error.message || String(error)}
            </pre>
            {stack && (
              <pre
                style={{
                  margin: '12px 0 0',
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: '#51605a',
                  maxHeight: 260,
                  overflow: 'auto',
                }}
              >
                {stack.trim()}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }
}
